import React, { useState, useCallback, useEffect } from 'react'
import { useModal, useModalExtra } from '../../../../_components/Modal'
import { useModifyClassList } from '../../../../common/utils/CSSUtils'
import { ChrofyShowErrors } from '../../../common/Form'
import { FILE_TYPE_NAME_MAX_LENGTH, FILE_TYPE_NAME_INVALID_CHARS_REGEX } from '../extra/constants'
import '../../../../assets/css/style.css'
import '../../../../assets/css/common.css'
import '../extra/types.js'

/**
 * ファイル種別管理モーダル（UI）
 *
 * @param {Object} props
 * @param {import('../extra/types').FileClassificationListStructure} props.fileClassificationList
 * @param {string} props.corporationNumber
 * @param {function(any, any): void} props.onChange
 */
export const FileClassificationModal = ({ fileClassificationList, corporationNumber, onChange }) => {
  const [localFileClassificationList, setLocalFileClassificationList] = useState(() => ({
    defaultTypes: [...(fileClassificationList.defaultTypes || [])],
    userTypes: [...(fileClassificationList.userTypes || [])]
  }))
  const [newTypeName, setNewTypeName] = useState('')
  const [errors, setErrors] = useState({})

  // バリデーション（保存ボタンクリック時に実行）
  const validateTypeName = useCallback(
    (name, options = {}) => {
      const nextErrors = {}
      const { checkDuplicate = true } = options

      if (!name || name.trim() === '') {
        nextErrors.name = '種別名を入力してください'
        return nextErrors
      }

      if (checkDuplicate) {
        const normalizedName = name.trim()
        const allTypes = [
          ...(localFileClassificationList.defaultTypes || []),
          ...(localFileClassificationList.userTypes || [])
        ]
        if (allTypes.some((ft) => (ft.fileClassification || '').trim() === normalizedName)) {
          nextErrors.name = '同じ名前の種別が既に存在します'
          return nextErrors
        }
      }

      if (name.length > FILE_TYPE_NAME_MAX_LENGTH) {
        nextErrors.name = `種別名は${FILE_TYPE_NAME_MAX_LENGTH}文字以内で入力してください`
        return nextErrors
      }
      if (FILE_TYPE_NAME_INVALID_CHARS_REGEX.test(name)) {
        nextErrors.name = '種別名に使用できない文字が含まれています（/ \\ : * ? " < > |）'
        return nextErrors
      }

      return nextErrors
    },
    [localFileClassificationList]
  )

  // ファイル種別の追加
  const handleAddFileType = useCallback(() => {
    const validationErrors = validateTypeName(newTypeName)
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    const newType = {
      fileClassification: newTypeName.trim(),
      isDefault: false,
      isInUse: false
    }

    setLocalFileClassificationList((prev) => ({
      ...prev,
      userTypes: [...(prev.userTypes || []), newType]
    }))
    setNewTypeName('')
    setErrors({})
  }, [newTypeName, validateTypeName])

  // ファイル種別の削除・復元（仕様: クライアントでは使用中チェックは行わず、削除可否は一括保存APIで判定）
  const handleToggleDeleteFileType = useCallback(
    (fileClassificationLabel, isRestore = false) => {
      const originalUserTypes = fileClassificationList.userTypes || []
      const localUserTypes = localFileClassificationList.userTypes || []

      const fileType = localUserTypes.find((ft) => ft.fileClassification === fileClassificationLabel)
      if (!fileType) return

      const isRegisteredItem = originalUserTypes.some((orig) => orig.fileClassification === fileClassificationLabel)

      if (isRegisteredItem) {
        if (!isRestore && fileType.isDefault) return

        setLocalFileClassificationList((prev) => ({
          ...prev,
          userTypes: (prev.userTypes || []).map((ft) => {
            if (ft.fileClassification === fileClassificationLabel) {
              return { ...ft, isPendingDelete: !isRestore }
            }
            return ft
          })
        }))
      } else {
        setLocalFileClassificationList((prev) => ({
          ...prev,
          userTypes: (prev.userTypes || []).filter((ft) => ft.fileClassification !== fileClassificationLabel)
        }))
      }
    },
    [fileClassificationList.userTypes, localFileClassificationList.userTypes]
  )

  // 外部にデータを通知（仕様: 現状のページ状態を一括で送る。全種別を file_classification_label + delete_flag で送信）
  useEffect(() => {
    const originalUserTypes = fileClassificationList.userTypes || []
    const localUserTypes = localFileClassificationList.userTypes || []

    const newTypes = localUserTypes.filter(
      (local) =>
        !local.isPendingDelete &&
        !originalUserTypes.find((orig) => orig.fileClassification === local.fileClassification)
    )

    const fullPayload = [
      ...(localFileClassificationList.defaultTypes || []).map((t) => ({
        file_classification_label: t.fileClassification,
        delete_flag: false
      })),
      ...(localFileClassificationList.userTypes || []).map((t) => ({
        file_classification_label: t.fileClassification,
        delete_flag: !!t.isPendingDelete
      }))
    ]

    onChange(
      {
        isValid: newTypes.every(
          (t) => Object.keys(validateTypeName(t.fileClassification, { checkDuplicate: false })).length === 0
        ),
        data: localFileClassificationList,
        payload: fullPayload
      },
      setErrors
    )
  }, [fileClassificationList.userTypes, localFileClassificationList, onChange, validateTypeName])

  return (
    <div>
      <div className="mb-8">
        <h3 className="text-base mb-4 font-bold text-[#3150A5]">新しい種別を追加</h3>
        <div className="flex gap-3">
          <input
            type="text"
            id="customFileTypeNameModal"
            className={`text-input text-left w-full flex-1 ${errors.name ? 'is-error border-red' : ''}`}
            placeholder="種別名を入力"
            value={newTypeName}
            onChange={(e) => {
              setNewTypeName(e.target.value)
              setErrors({})
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleAddFileType()
              }
            }}
          />
          <button
            type="button"
            className="btn-style-primary-action text-h7 font-medium whitespace-nowrap shrink-0"
            onClick={handleAddFileType}
          >
            + 追加
          </button>
        </div>
        <ChrofyShowErrors field="name" errors={errors} />
      </div>

      <div>
        <h3 className="text-base mb-4 font-bold text-[#3150A5]">登録されている種別</h3>
        <div className="mb-5">
          <h4 className="text-sm text-gray-600 mb-2.5">デフォルト種別</h4>
          <div className="flex flex-wrap gap-2">
            {(localFileClassificationList.defaultTypes || []).map((fileType) => (
              <span
                key={fileType.fileClassification}
                className="inline-flex items-center gap-2 bg-[#e8f4f8] px-3.5 py-1.5 rounded-2xl text-[13px] text-[#3150A5] border border-[#3150A5]"
              >
                {fileType.fileClassification}
              </span>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-sm text-gray-600 mb-2.5">追加した種別</h4>
          <div className="flex flex-wrap gap-2 min-h-[40px]">
            {(localFileClassificationList.userTypes || []).length === 0 ? (
              <span className="text-gray-400 text-sm">追加された種別はありません</span>
            ) : (
              (localFileClassificationList.userTypes || []).map((fileType) => {
                const canDelete = !fileType.isDefault
                const isPendingDelete = !!fileType.isPendingDelete
                const isRegisteredItem = (fileClassificationList.userTypes || []).some(
                  (orig) => orig.fileClassification === fileType.fileClassification
                )

                return (
                  <span
                    key={fileType.fileClassification}
                    className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-2xl text-[13px] border ${
                      isPendingDelete && isRegisteredItem
                        ? 'bg-[#f3f4f6] border-gray-300 grey--text line-through'
                        : 'bg-[#f8f9fa] border-gray-300'
                    }`}
                  >
                    {fileType.fileClassification}
                    {isRegisteredItem && isPendingDelete ? (
                      <button
                        onClick={() => handleToggleDeleteFileType(fileType.fileClassification, true)}
                        className="bg-transparent border-none cursor-pointer flex items-center justify-center p-0 text-[#2E7D4F] hover:opacity-70"
                        type="button"
                        title="復帰"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M21 3v5h-5"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M3 21v-5h5"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    ) : (
                      canDelete && (
                        <button
                          onClick={async () => {
                            await handleToggleDeleteFileType(fileType.fileClassification)
                          }}
                          className="bg-transparent border-none cursor-pointer font-bold text-sm leading-none hover:opacity-70 p-0 text-[#dc3545]"
                          type="button"
                        >
                          ×
                        </button>
                      )
                    )}
                  </span>
                )
              })
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * ファイル種別管理モーダルを開くフック
 *
 * @param {import('../extra/types').FileClassificationListStructure} fileClassificationList
 * @param {function(import('../extra/types').FileClassificationListStructure, any[]): Promise<void>} onSave
 * @param {string} corporationNumber
 * @returns {function(): Promise<void>}
 */
export const useFileClassification = (fileClassificationList, onSave, corporationNumber) => {
  const showModal = useModal()
  const { _closeUpModal } = useModalExtra()
  const { promisify } = useModifyClassList('.modal-window', ['w-[728px]'])

  const openFileClassificationModal = () => {
    let modalData = {}
    let setModalErrors = () => {}

    const onChange = (data, setErrors) => {
      modalData = data
      setModalErrors = setErrors
    }

    return promisify((res) => {
      showModal({
        title: 'ファイル種別の管理',
        description: (
          <div className="text-caption-2 grey--text text-gray-600 text-center mt-2 mb-5 [text-wrap:pretty]">
            月ごとにファイル名が変わる場合でも設定を再利用できるよう、各ファイルに種別を設定してください。
          </div>
        ),
        isCancel: false,
        isWide: false,
        isSpinnerDisabled: true,
        onClose: res,
        body: (
          <FileClassificationModal
            fileClassificationList={fileClassificationList}
            corporationNumber={corporationNumber}
            onChange={onChange}
          />
        ),
        button: [
          {
            name: 'キャンセル',
            className: 'btn footer-btn button_back',
            click: () => {},
            close: true
          },
          {
            name: '登録',
            className: 'btn footer-btn button_base',
            close: false,
            click: async () => {
              if (!modalData.isValid) {
                setModalErrors({ name: '種別名のバリデーションエラーがあります' })
                return
              }

              try {
                if (modalData.payload.length > 0) {
                  await onSave(modalData.data, modalData.payload)
                }
                _closeUpModal()
                res()
              } catch (e) {
                console.error(e)
                setModalErrors({ name: 'ファイル種別の保存に失敗しました。' })
              }
            }
          }
        ]
      })
    })
  }

  return openFileClassificationModal
}
